/**
 * Notification Configuration API Wrapper
 *
 * Centralized API calls for Notification Configuration feature:
 * - Entity list fetching (Employees, Clients, Providers, CorporateEntities)
 * - Module configuration loading and saving
 */

import Api from 'axiosApi/api';
import { AxiosResponse } from 'axios';
import { ModuleActionConfigAndSchemaResponse } from 'axiosApi/models/module-action-config-and-schema-response';
import { UpdateEntireConfig } from 'axiosApi/models/update-entire-config';

export class NotificationConfigApiWrapper {
  private api: Api;

  constructor(api: Api) {
    this.api = api;
  }

  /**
   * Fetch all employees
   * GET /api/v1/ListEmployees
   */
  async fetchEmployees(version: string = '1'): Promise<AxiosResponse<any>> {
    try {
      return await this.api.employeeApi.apiVversionListEmployeesGet(version, true, {});
    } catch (error) {
      console.error('Failed to fetch employees:', error);
      throw error;
    }
  }

  /**
   * Fetch all clients
   * GET /api/v1/ListClients
   */
  async fetchClients(version: string = '1'): Promise<AxiosResponse<any>> {
    try {
      return await this.api.clientsApi.apiVversionListClientsGet(version, {});
    } catch (error) {
      console.error('Failed to fetch clients:', error);
      throw error;
    }
  }

  async fetchScopes(version: string = '1'): Promise<AxiosResponse<any>> {
    try {
      return await this.api.employeeApi.apiVversionScopeAllGet(version, 0, 200);
    } catch (error) {
      console.error('Failed to fetch providers:', error);
      throw error;
    }
  }

  /**
   * Fetch all providers
   * GET /api/v1/ListProviders (using typeahead endpoint for now)
   */
  async fetchProviders(version: string = '1'): Promise<AxiosResponse<any>> {
    try {
      return await this.api.employeeApi.apiVversionListProvidersGet(version, {});
    } catch (error) {
      console.error('Failed to fetch providers:', error);
      throw error;
    }
  }

  /**
   * Fetch all corporate entities
   * GET /api/v1/CorporateEntity/All
   */
  async fetchCorporateEntities(version: string = '1'): Promise<AxiosResponse<any>> {
    try {
      return await this.api.invoiceApi.apiVversionCorporateEntityAllGet(version, 1, 20);
    } catch (error) {
      console.error('Failed to fetch corporate entities:', error);
      throw error;
    }
  }

  /**
   * Get entire notification configuration
   * GET /api/v1/Module/GetAllActionsConfigAndSchema
   *
   * Returns the modules configuration directly (unwrapped from response envelope)
   */
  async getEntireConfig(): Promise<ModuleActionConfigAndSchemaResponse> {
    try {
      const response = await this.api.notificationsApi.apiV1ModuleGetAllActionsConfigAndSchemaGet({});

      if (response.data?.data) {
        return response.data.data;
      }
      
      // Return empty structure if no data
      return { modules: null };
    } catch (error) {
      console.error('Failed to fetch module config:', error);
      throw error;
    }
  }

  /**
   * Save notification module configuration
   * PUT /api/v1/NotificationConfig/configuration
   *
   */
  async saveModuleConfig(version: string = '1', payload: UpdateEntireConfig): Promise<AxiosResponse<any>> {
    try {
      return await this.api.notificationsApi.apiVversionNotificationConfigConfigurationPut(version, payload, {});
    } catch (error) {
      console.error('Failed to save module config:', error);
      throw error;
    }
  }

  /**
   * Fetch all entity lists in parallel
   * Returns all lists needed for the notification configuration UI
   */
  async fetchAllEntities(version: string = '1'): Promise<{
    employees: any[];
    clients: any[];
    providers: any[];
    corporateEntities: any[];
    scopes : any[];
  }> {
    try {
      const [employeesRes, clientsRes, providersRes, corporateEntitiesRes, scopesRes] = await Promise.all([
        this.fetchEmployees(version),
        this.fetchClients(version),
        this.fetchProviders(version),
        this.fetchCorporateEntities(version),
        this.fetchScopes(version)
      ]);

      return {
        employees: employeesRes.data?.data?.queryResult || employeesRes.data?.data || [],
        clients: clientsRes.data?.data?.queryResult || clientsRes.data?.data || [],
        providers: providersRes.data?.data?.queryResult || providersRes.data?.data || [],
        corporateEntities: corporateEntitiesRes.data?.data?.queryResult || corporateEntitiesRes.data?.data || [],
        scopes : scopesRes.data?.data?.queryResult  || scopesRes.data?.data || []
      };
    } catch (error) {
      console.error('Failed to fetch all entities:', error);
      throw error;
    }
  }
}
